import type { ILead } from 'src/services/api';
import type { IProperty } from 'src/services/api';

const normalizePhoneNumber = (phoneNumber: string) => {
    const numberChars = phoneNumber.split('').filter((char) => char >= '0' && char <= '9');
    return numberChars.join('');
};

const getLeadSearchTokens = (leads: ILead[] | undefined, properties: IProperty[] | undefined) => {
    if (!leads) {
        return [];
    }

    return leads.reduce<[ILead, string[]][]>((acc, lead) => {
        const phoneNumber = lead.phone_number || '';

        const uuid = lead.uuid;
        const name = lead.name || '';
        const email = lead.email || '';
        const phoneNumberNormalized = phoneNumber.replace(/\D/g, '');
        const notes = lead.notes?.contents || '';
        const stage = lead.stage.split('_');

        const property = properties?.find((o) => o.id === lead.property_id);
        const propertyId = property?.id ? String(property.id) : '';
        const isPropertyVacant = property?.is_vacant;
        const propertyVacancyStatus = isPropertyVacant == null ? '' : isPropertyVacant ? 'vacant' : 'occupied';
        const propertyName = property?.name || '';
        const labels = lead.labels?.map((label) => label.name);

        const tokens = [
            uuid,
            name,
            email,
            phoneNumber,
            phoneNumberNormalized,
            notes,
            ...stage,
            propertyId,
            propertyVacancyStatus,
            propertyName,
            ...labels,
        ].filter(Boolean).map((token) => token.toLowerCase());

        acc.push([lead, tokens]);

        return acc;
    }, []);
};

/**
 * Recognize a few common patterns of phone numbers
 * We can use this to optimize search by prioritizing phone number search over tokenized search
 */
export const isPhoneNumberSearchQuery = (searchQuery: string) => {
    const normPhoneNumber = normalizePhoneNumber(searchQuery);

    // Only treat as pure phone number search if normalized is 10-11 digits AND input has no letters
    if ((normPhoneNumber.length === 10 || normPhoneNumber.length === 11) &&
        !/[a-z]/i.test(searchQuery)) {
        return true;
    }

    return false;
};

export const searchLeads = (
    leads: ILead[] | undefined,
    properties: IProperty[] | undefined,
    searchQuery: string,
) => {
    searchQuery = searchQuery.toLowerCase().trim();

    if (!leads) {
        return [];
    }

    if (!searchQuery) {
        return leads;
    }

    const leadSearchTokens = getLeadSearchTokens(leads, properties);

    const filteredLeads: ILead[] = [];
    let searchQueryTokens: string[];

    if (isPhoneNumberSearchQuery(searchQuery)) {
        // if we are searching for a phone number, no point in tokenizing the search query
        // normalize it and search for exact matches
        const normQ = normalizePhoneNumber(searchQuery);
        searchQueryTokens = [normQ];
    } else {
        searchQueryTokens = searchQuery
            .split(' ')
            .map((token) => token.trim())
            .filter(Boolean);
    }

    for (const [lead, leadTokens] of leadSearchTokens) {
        let foundAllSearchTokens = true;

        for (const searchToken of searchQueryTokens) {
            let foundSearchToken = false;

            for (const leadToken of leadTokens) {
                if (leadToken.includes(searchToken)) {
                    foundSearchToken = true;
                    break;
                }
            }

            if (!foundSearchToken) {
                foundAllSearchTokens = false;
                break;
            }
        }

        if (foundAllSearchTokens) {
            filteredLeads.push(lead);
        }
    }

    return filteredLeads;
};